import * as THREE from 'three';

export type FlatLabelTextureResult = {
  texture: THREE.CanvasTexture;
  planeW: number;
  planeH: number;
};

/**
 * Rasterise floor-plan label text for a flat 3D plane.
 * Matches 2D FloorPlanLabels: sans-serif, left/middle anchor, cap height = font size.
 */
export function createFlatLabelTexture(
  text: string,
  color: string,
  capHeight: number,
  options?: { outline?: boolean }
): FlatLabelTextureResult {
  const planeH = Math.max(capHeight, 1);
  const dpr = typeof window !== 'undefined' ? Math.min(window.devicePixelRatio || 1, 2) : 2;
  const fontSizePx = planeH * dpr;

  const measureCanvas = document.createElement('canvas');
  const measureCtx = measureCanvas.getContext('2d');
  let textWidthPx = text.length * fontSizePx * 0.55;
  if (measureCtx) {
    measureCtx.font = `${fontSizePx}px sans-serif`;
    textWidthPx = measureCtx.measureText(text).width;
  }

  const canvasW = Math.min(Math.max(Math.ceil(textWidthPx + 2), 8), 4096);
  const canvasH = Math.min(Math.max(Math.ceil(fontSizePx * 1.15), 8), 512);

  const canvas = document.createElement('canvas');
  canvas.width = canvasW;
  canvas.height = canvasH;

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    const tex = new THREE.CanvasTexture(canvas);
    return { texture: tex, planeW: planeH, planeH };
  }

  ctx.clearRect(0, 0, canvasW, canvasH);
  ctx.font = `${fontSizePx}px sans-serif`;
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'left';

  const y = canvasH / 2;

  if (options?.outline) {
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = Math.max(1, fontSizePx * 0.1);
    ctx.lineJoin = 'round';
    ctx.strokeText(text, 0, y, canvasW);
  }

  ctx.fillStyle = color;
  ctx.fillText(text, 0, y, canvasW);

  const planeW = canvasW / dpr;

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.needsUpdate = true;

  return { texture, planeW, planeH };
}
